import * as React from 'react';
import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AdminLayout, { AdminSection } from '../../components/AdminLayout';
import axiosInstance from '../../utils/axiosInstance';

const emptyPolicy = {
  inactivity_reminder_days: 30,
  suspension_warning_days: 10,
  suspension_after_days: 40,
  retention_after_days: 180,
  reminders_enabled: true,
  suspension_enabled: true,
  metadata: {},
};

function toPolicyForm(policy) {
  return {
    ...emptyPolicy,
    ...policy,
    inactivity_reminder_days: String(policy?.inactivity_reminder_days ?? emptyPolicy.inactivity_reminder_days),
    suspension_warning_days: String(policy?.suspension_warning_days ?? emptyPolicy.suspension_warning_days),
    suspension_after_days: String(policy?.suspension_after_days ?? emptyPolicy.suspension_after_days),
    retention_after_days: String(policy?.retention_after_days ?? emptyPolicy.retention_after_days),
    reminders_enabled: Boolean(policy?.reminders_enabled ?? emptyPolicy.reminders_enabled),
    suspension_enabled: Boolean(policy?.suspension_enabled ?? emptyPolicy.suspension_enabled),
  };
}

function numericValue(value) {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

export default function AdminAccountSecurityPage(props) {
  const [state, setState] = useState({ loading: true, error: '', policy: null });
  const [form, setForm] = useState(toPolicyForm(emptyPolicy));
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState('');

  async function loadPolicy() {
    setState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const response = await axiosInstance.get('/admin/account-security');
      const policy = response.data?.policy || emptyPolicy;
      setState({ loading: false, error: '', policy });
      setForm(toPolicyForm(policy));
    } catch (error) {
      setState({
        loading: false,
        error: error?.response?.data?.message || 'Unable to load account security policy.',
        policy: null,
      });
    }
  }

  useEffect(() => {
    loadPolicy();
  }, []);

  async function savePolicy(event) {
    event.preventDefault();
    setSaving(true);
    setFeedback('');
    setState((current) => ({ ...current, error: '' }));
    try {
      const response = await axiosInstance.patch('/admin/account-security', {
        inactivity_reminder_days: numericValue(form.inactivity_reminder_days),
        suspension_warning_days: numericValue(form.suspension_warning_days),
        suspension_after_days: numericValue(form.suspension_after_days),
        retention_after_days: numericValue(form.retention_after_days),
        reminders_enabled: form.reminders_enabled,
        suspension_enabled: form.suspension_enabled,
        metadata: form.metadata || {},
      });
      const policy = response.data?.policy || emptyPolicy;
      setState({ loading: false, error: '', policy });
      setForm(toPolicyForm(policy));
      setFeedback('Account security policy updated.');
    } catch (error) {
      setState((current) => ({
        ...current,
        error: error?.response?.data?.message || 'Unable to update account security policy.',
      }));
    } finally {
      setSaving(false);
    }
  }

  const warningStart = Math.max(
    numericValue(form.suspension_after_days) - numericValue(form.suspension_warning_days),
    numericValue(form.inactivity_reminder_days),
  );

  return (
    <AdminLayout
      props={props}
      breadcrumbItems={[{ label: 'AMX xConsole', href: '/' }, { label: 'Admin Dashboard', href: '/admin' }, { label: 'Account Security' }]}
      title="Account Security"
      subtitle="Configure inactivity reminders, suspension windows, and data-retention timing for AMX accounts."
    >
      {state.loading ? (
        <Stack direction="row" spacing={2} alignItems="center">
          <CircularProgress size={24} />
          <Typography>Loading account security policy...</Typography>
        </Stack>
      ) : (
        <>
          {feedback ? <Alert severity="success">{feedback}</Alert> : null}
          {state.error ? <Alert severity="error">{state.error}</Alert> : null}

          <AdminSection title="Inactive Account Policy" subtitle="Policy changes apply to account-security evaluations shown to partner admins.">
            <Stack component="form" spacing={2.5} onSubmit={savePolicy}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="Reminder After Days"
                  type="number"
                  value={form.inactivity_reminder_days}
                  onChange={(event) => setForm({ ...form, inactivity_reminder_days: event.target.value })}
                  inputProps={{ min: 1 }}
                  fullWidth
                  required
                />
                <TextField
                  label="Suspension Warning Days"
                  type="number"
                  value={form.suspension_warning_days}
                  onChange={(event) => setForm({ ...form, suspension_warning_days: event.target.value })}
                  inputProps={{ min: 1 }}
                  fullWidth
                  required
                />
                <TextField
                  label="Suspend After Days"
                  type="number"
                  value={form.suspension_after_days}
                  onChange={(event) => setForm({ ...form, suspension_after_days: event.target.value })}
                  inputProps={{ min: 1 }}
                  fullWidth
                  required
                />
                <TextField
                  label="Retention After Days"
                  type="number"
                  value={form.retention_after_days}
                  onChange={(event) => setForm({ ...form, retention_after_days: event.target.value })}
                  inputProps={{ min: 1 }}
                  fullWidth
                  required
                />
              </Stack>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.reminders_enabled}
                      onChange={(event) => setForm({ ...form, reminders_enabled: event.target.checked })}
                    />
                  }
                  label="Send inactivity reminders"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.suspension_enabled}
                      onChange={(event) => setForm({ ...form, suspension_enabled: event.target.checked })}
                    />
                  }
                  label="Enable suspension evaluation"
                />
              </Stack>

              <Alert severity="info">
                With these values, users enter the warning window after {warningStart} inactive days and become eligible for suspension after {form.suspension_after_days} inactive days.
              </Alert>

              <Stack direction="row" justifyContent="flex-end">
                <Button type="submit" variant="contained" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Policy'}
                </Button>
              </Stack>
            </Stack>
          </AdminSection>
        </>
      )}
    </AdminLayout>
  );
}
